import React, { useState, useEffect, useRef } from 'react';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';

const API = '/api/admin/notifications/';

const formatTime = (t) => (t ? new Date(t).toLocaleString() : '');

const NotificationBell = () => {
  const { permissions } = usePermissions();
  const isSuperAdmin = !!permissions?.is_superuser;

  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const prevUnreadRef = useRef(0);

  const fetchCount = async () => {
    try {
      const res = await fetch(`${API}unread-count/`, { headers: getAuthHeaders(false) });
      if (res.ok) {
        const data = await res.json();
        setUnread(data.count || 0);
      }
    } catch {}
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}?me=true&unread=true`, { headers: getAuthHeaders(false) });
      if (res.ok) {
        const data = await res.json();
        setItems(Array.isArray(data) ? data : data.results || []);
      }
    } catch {}
    setLoading(false);
  };

  const acknowledge = async (id) => {
    try {
      const res = await fetch(`${API}${id}/acknowledge/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchCount();
        fetchItems();
        window.dispatchEvent(new CustomEvent('notification-acknowledged', { detail: { id } }));
      }
    } catch {}
  };

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) fetchItems();
  }, [open]);

  useEffect(() => {
    const handleFund = () => {
      fetchCount();
      if (open) fetchItems();
    };
    window.addEventListener('fund-request-notification-sent', handleFund);
    return () => window.removeEventListener('fund-request-notification-sent', handleFund);
  }, [open]);

  useEffect(() => {
    if (unread > prevUnreadRef.current && unread > 0) {
      setOpen(true);
    }
    prevUnreadRef.current = unread;
  }, [unread]);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [open]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        className="logout-btn"
        title="Notifications"
        style={{
          marginRight: '10px',
          background: 'transparent',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.5)',
          fontSize: 16,
          padding: '4px 10px',
          position: 'relative'
        }}
      >
        <i className="fa-solid fa-bell"></i>
        {unread > 0 && (
          <span style={{
            position: 'absolute',
            top: -6,
            right: -8,
            background: '#dc3545',
            color: '#fff',
            borderRadius: '50%',
            padding: '1px 5px',
            fontSize: 10,
            fontWeight: 'bold',
            lineHeight: 1
          }}>
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: 360,
          maxHeight: 450,
          overflowY: 'auto',
          backgroundColor: '#fff',
          color: '#333',
          borderRadius: 8,
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          zIndex: 9999
        }}>
          <div style={{
            padding: '10px 12px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f9fafb'
          }}>
            <strong style={{ fontSize: 14 }}>Notifications</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isSuperAdmin && (
                <button
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('show-payables-notification'));
                    setOpen(false);
                  }}
                  style={{ fontSize: 11, padding: '3px 8px', cursor: 'pointer' }}
                >
                  Payables
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}
              >×</button>
            </div>
          </div>

          {loading && (
            <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>Loading...</div>
          )}

          {!loading && items.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>No new notifications</div>
          )}

          {!loading && items.map(n => (
            <div key={n.id} style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>{n.title}</div>
              <div style={{ fontSize: 12, color: '#4b5563', whiteSpace: 'pre-line', lineHeight: 1.4 }}>{n.message}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span style={{ fontSize: 10, color: '#9ca3af' }}>{formatTime(n.triggered_on)}</span>
                <button
                  onClick={() => acknowledge(n.id)}
                  style={{ fontSize: 11, padding: '3px 8px', cursor: 'pointer', backgroundColor: n.notification_type === 'QUICK_REFERENCE_APPROVAL' ? '#f59e0b' : '#3f6b32', color: '#fff', border: 'none', borderRadius: 4 }}
                >
                  Acknowledge
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
